<template>
    <div
        class="group flex h-fit w-full cursor-pointer flex-col items-center justify-center gap-y-4 rounded-muval-2 px-4 py-6 transition-colors duration-150"
        :class="{ 'bg-muval-sky-1/5': isDragOver }"
        :style="{
            backgroundImage: dashedBorder(isDragOver),
            backgroundRepeat: 'no-repeat',
            backgroundSize: '100% 100%',
        }"
        @dragover.prevent="isDragOver = true"
        @dragleave.prevent="isDragOver = false"
        @drop.prevent="onDrop"
        @click="emit('select')"
    >
        <div class="card grid size-10 place-items-center">
            <SvgIcon
                name="system-upload"
                class="size-6 text-muval-gray-2 transition-colors group-hover:text-muval-gray-1"
            />
        </div>
        <span class="text-sm text-muval-gray-2">
            <button
                type="button"
                class="inline-block font-medium text-brand transition-colors hover:text-brand-shade"
            >
                Click to upload</button
            ><!--
                Extra inline actions sit between the upload link and the trailing
                copy - the order tab's "generate" is the only one so far.
            --><slot name="actions" />
            or drag and drop
        </span>
    </div>
</template>

<script setup lang="ts">
import SvgIcon from '@components/utilities/SvgIcon.vue';

/**
 * The dashed upload target shared by every file list.
 *
 * A `div` rather than a `button`: the slot may hold its own controls, and a
 * button inside a button is not valid markup.
 */

const emit = defineEmits<{ select: []; drop: [files: File[]] }>();

const isDragOver = ref(false);

const DASHED_BORDER_COLOR = '%23999999';
const DASHED_BORDER_HOVER_COLOR = '%230EA5E9';

const dashedBorder = (hovered: boolean): string => {
    const stroke = hovered ? DASHED_BORDER_HOVER_COLOR : DASHED_BORDER_COLOR;

    return `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='100%25' height='100%25'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='${stroke}' stroke-width='1' stroke-dasharray='2%2c2'/%3e%3c/svg%3e")`;
};

/**
 * `items` rather than `files`, matching the order tab: a drag from another
 * browser window can carry non-file items, which `dataTransfer.files` reports as
 * zero-byte entries.
 */
const onDrop = (event: DragEvent) => {
    isDragOver.value = false;

    const dropped = Array.from(event.dataTransfer?.items ?? [])
        .map((item) => (item.kind === 'file' ? item.getAsFile() : null))
        .filter((file): file is File => !!file);

    if (dropped.length) {
        emit('drop', dropped);
    }
};
</script>
